import { INCENTIVE_AWARD_HELP, INCENTIVE_PENALTY_HELP } from "@/lib/incentives/incentive-help";
import { FREE_LATE_SIGNOFFS, lateSignOffPenalty } from "@/lib/time/signoff-rules";
import type { IncentiveGoal, IncentiveYear, MetricFormat } from "@/schemas/incentives";

/**
 * My Incentives display rules. Scorecard lines and reductions are shown as Incentyv stored
 * them; the one calculation the app makes is the 2026 plan's final award (`finalAward`,
 * DECISIONS 2026-10-15). Incentyv's stored FinalAwardPercent subtracts points and is never
 * read.
 */

const DASH = "—";

const percent = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const twoDecimals = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** A stored fraction as a percent: 0.625 → "62.5%". Never capped; null → "—". */
export function formatAwardPercent(value: number | null): string {
  if (value === null) return DASH;
  return `${percent.format(value * 100)}%`;
}

/** A metric value (min, target, max) in its msMetricFormat. Currency is whole dollars. */
export function formatMetricValue(value: number | null, format: MetricFormat): string {
  if (value === null) return DASH;
  switch (format) {
    case "percent":
      return formatAwardPercent(value);
    case "currencywhole":
    case "currencydecimal":
      return `$${whole.format(value)}`;
    case "numberwhoel":
      return whole.format(value);
    case "numberdecimal":
      return twoDecimals.format(value);
    default:
      return String(value);
  }
}

/** "1% / 3% / 8%": only stored values; a missing min or max is a dash, never derived. */
export function goalRange(goal: IncentiveGoal): string {
  return [goal.min, goal.target, goal.max]
    .map((v) => formatMetricValue(v, goal.format))
    .join(" / ");
}

const sumAwarded = (goals: IncentiveGoal[]) => goals.reduce((s, g) => s + (g.awarded ?? 0), 0);

export interface FinalAward {
  /** Σ Awarded on the Company goals. */
  company: number;
  /** Σ Awarded on the Personal goals. */
  individual: number;
  /** company + individual: the sum of Awarded. */
  earned: number;
  /** Σ stored reductions (a missing value counts as 0). */
  reductions: number;
  /** earned × (1 − reductions), never below 0. */
  final: number;
}

/**
 * The headline, per the 2026 Variable Compensation plan (Zack, 2026-10-15): the earned award
 * (sum of Awarded) reduced by the total of the stored reductions, multiplicatively, clamped so
 * it is never below 0. This matches finance's dollar-side accrual ratio, not Incentyv's stored
 * final (which subtracts the reductions as points).
 */
export function finalAward(year: IncentiveYear): FinalAward {
  const company = sumAwarded(year.companyGoals);
  const individual = sumAwarded(year.personalGoals);
  const earned = company + individual;
  const reductions = year.penalties.reduce((s, p) => s + (p.value ?? 0), 0);
  const final = Math.max(0, earned * (1 - Math.min(reductions, 1)));
  return { company, individual, earned, reductions, final };
}

/** "62.5% earned × (1 − 61%)": the headline's formula with the person's numbers. */
export function formulaLine(award: FinalAward): string {
  const pct = formatAwardPercent;
  return `${pct(award.earned)} earned × (1 − ${pct(award.reductions)})`;
}

/** The Late submissions tile: "3 of 5 free" within the allowance, "7 · 2% off" beyond it. */
export function lateTileText(count: number): string {
  if (count <= FREE_LATE_SIGNOFFS) return `${count} of ${FREE_LATE_SIGNOFFS} free`;
  return `${count} · ${formatAwardPercent(lateSignOffPenalty(count))} off`;
}

/** The plan's help line for a reduction type (CompModifierType.Enum). */
export function penaltyHelp(typeEnum: string | null): string {
  switch (typeEnum) {
    case "Profit Safeguard":
      return INCENTIVE_PENALTY_HELP.profitSafeguard;
    case "Utilization":
      return INCENTIVE_PENALTY_HELP.utilization;
    case "Late Time Entry":
      return INCENTIVE_PENALTY_HELP.lateTimeEntry;
    default:
      return INCENTIVE_PENALTY_HELP.other;
  }
}

export interface BreakdownRow {
  key: string;
  label: string;
  value: number;
  kind: "share" | "subtotal" | "penalty" | "total";
  help: string;
}

/** Company, Individual, Earned award, each reduction as stored, then the Final award. */
export function awardBreakdown(year: IncentiveYear): BreakdownRow[] {
  const award = finalAward(year);
  return [
    {
      key: "company",
      label: "Company",
      value: award.company,
      kind: "share",
      help: INCENTIVE_AWARD_HELP.split,
    },
    {
      key: "individual",
      label: "Individual",
      value: award.individual,
      kind: "share",
      help: INCENTIVE_AWARD_HELP.split,
    },
    {
      key: "earned",
      label: "Earned award",
      value: award.earned,
      kind: "subtotal",
      help: INCENTIVE_AWARD_HELP.earned,
    },
    ...year.penalties.map(
      (p): BreakdownRow => ({
        key: p.id,
        label: p.type,
        value: p.value ?? 0,
        kind: "penalty",
        help: penaltyHelp(p.typeEnum),
      }),
    ),
    {
      key: "final",
      label: "Final award",
      value: award.final,
      kind: "total",
      help: INCENTIVE_AWARD_HELP.final,
    },
  ];
}

/** The years the switcher offers: the person's scorecard years plus the current year. */
export function incentiveYearOptions(dataYears: number[], currentYear: number): number[] {
  return [...new Set([...dataYears, currentYear])].sort((a, b) => a - b);
}

/** The current year when it has a scorecard, else the latest year that does. */
export function defaultIncentiveYear(dataYears: number[], currentYear: number): number {
  if (dataYears.includes(currentYear) || dataYears.length === 0) return currentYear;
  return Math.max(...dataYears);
}

/** The neighbouring options of `year` (which may itself not be an option). */
export function incentiveYearSteps(
  year: number,
  options: number[],
): { prev: number | null; next: number | null } {
  const before = options.filter((y) => y < year);
  const after = options.filter((y) => y > year);
  return { prev: before.at(-1) ?? null, next: after[0] ?? null };
}
